/**
 * Market Insights Component
 * Displays market statistics, forecasts, and property estimates
 */

import React, { useState } from 'react';
import { useMarketAnalytics } from '../../agents/marketAnalyticsAgent';
import './MarketInsightsComponent.css';

export default function MarketInsightsComponent() {
  const {
    stats,
    forecast,
    insights,
    aiInsights,
    isLoading,
    aiLoading,
    error,
    aiError,
    getMarketStats,
    getPriceForecast,
    getMarketInsights,
    getAIInsights,
  } = useMarketAnalytics();

  const [city, setCity] = useState('Austin');
  const [state, setState] = useState('TX');
  const [activeTab, setActiveTab] = useState('stats');

  const TAB_KEYS = ['stats', 'forecast', 'insights', 'ai-insights'];

  const handleTabKeyDown = (e, currentKey) => {
    const currentIdx = TAB_KEYS.indexOf(currentKey);
    let nextIdx = currentIdx;

    if (e.key === 'ArrowRight') {
      nextIdx = (currentIdx + 1) % TAB_KEYS.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (currentIdx - 1 + TAB_KEYS.length) % TAB_KEYS.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = TAB_KEYS.length - 1;
    } else {
      return; // don't prevent default for other keys
    }

    e.preventDefault();
    const nextKey = TAB_KEYS[nextIdx];
    setActiveTab(nextKey);
    document.getElementById(`tab-${nextKey}`)?.focus();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!city.trim() || !state.trim()) return;
    // Fire AI insights in the background — don't block the main search
    getAIInsights(city, state);
    await Promise.all([
      getMarketStats(city, state),
      getMarketInsights(city, state),
      getPriceForecast(city, state),
    ]);
  };

  return (
    <div className="market-insights-container">
      <h2>Market Analytics & Insights</h2>

      <form onSubmit={handleSearch} className="search-form">
        <div className="form-group">
          <label htmlFor="market-city" className="form-label">City</label>
          <input
            id="market-city"
            type="text"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="form-input"
            required
            disabled={isLoading}
          />
          <label htmlFor="market-state" className="form-label">State</label>
          <input
            id="market-state"
            type="text"
            placeholder="State"
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            className="form-input"
            maxLength="2"
            required
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading} className="search-button">
            {isLoading ? 'Loading...' : 'Get Market Data'}
          </button>
        </div>
      </form>

      {error && <div className="error-message">{error}</div>}

      {stats && (
        <div className="market-tabs">
          <div className="tab-buttons" role="tablist">
            {[
              { key: 'stats', label: 'Statistics' },
              { key: 'forecast', label: 'Forecast' },
              { key: 'insights', label: 'Insights' },
              { key: 'ai-insights', label: 'AI Insights' },
            ].map(({ key, label }) => (
              <button
                key={key}
                id={`tab-${key}`}
                role="tab"
                aria-selected={activeTab === key}
                aria-controls={`panel-${key}`}
                className={`tab-button ${activeTab === key ? 'active' : ''}`}
                tabIndex={activeTab === key ? 0 : -1}
                onClick={() => setActiveTab(key)}
                onKeyDown={(e) => handleTabKeyDown(e, key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            <div
              id="panel-stats"
              role="tabpanel"
              aria-labelledby="tab-stats"
              hidden={activeTab !== 'stats'}
            >
              {stats && (
                <div className="market-stats">
                  <div className="stat-card">
                    <h3>Median Price</h3>
                    <p className="stat-value">
                      ${stats.price_stats.median?.toLocaleString() || 'N/A'}
                    </p>
                  </div>

                  <div className="stat-card">
                    <h3>Average Price/SqFt</h3>
                    <p className="stat-value">
                      ${stats.price_per_sqft.mean?.toFixed(2) || 'N/A'}
                    </p>
                  </div>

                  <div className="stat-card">
                    <h3>Market Heat</h3>
                    <p className={`stat-value heat-uppercase heat-${stats.market_heat_index.level}`}>
                      {stats.market_heat_index.level}
                    </p>
                  </div>

                  <div className="stat-card">
                    <h3>Market Trend</h3>
                    <p className={`stat-value trend-uppercase trend-${stats.market_trend.trend}`}>
                      {stats.market_trend.trend}
                    </p>
                    <p className="stat-detail">
                      {stats.market_trend.change_percent > 0 ? '+' : ''}
                      {stats.market_trend.change_percent}%
                    </p>
                  </div>

                  <div className="stat-card">
                    <h3>Avg Days on Market</h3>
                    <p className="stat-value">
                      {stats.days_on_market.average?.toFixed(0) || 'N/A'} days
                    </p>
                  </div>

                  <div className="stat-card">
                    <h3>Total Listings</h3>
                    <p className="stat-value">
                      {stats.total_listings}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div
              id="panel-forecast"
              role="tabpanel"
              aria-labelledby="tab-forecast"
              hidden={activeTab !== 'forecast'}
            >
              {forecast && (
                <div className="market-forecast">
                  <h3>Price Forecast</h3>
                  <div className="forecast-grid">
                    <div className="forecast-card">
                      <p className="forecast-period">30 Days</p>
                      <p className="forecast-price">
                        ${forecast.forecast['30_days']?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div className="forecast-card">
                      <p className="forecast-period">90 Days</p>
                      <p className="forecast-price">
                        ${forecast.forecast['90_days']?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div className="forecast-card">
                      <p className="forecast-period">6 Months</p>
                      <p className="forecast-price">
                        ${forecast.forecast['6_months']?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div className="forecast-card">
                      <p className="forecast-period">12 Months</p>
                      <p className="forecast-price">
                        ${forecast.forecast['12_months']?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              id="panel-insights"
              role="tabpanel"
              aria-labelledby="tab-insights"
              hidden={activeTab !== 'insights'}
            >
              {insights && (
                <div className="market-insights">
                  <h3>Market Insights</h3>
                  <div className="insights-list">
                    {insights.insights.map((insight, idx) => (
                      <div key={idx} className="insight-item">
                        <span className="insight-icon">💡</span>
                        <p>{insight}</p>
                      </div>
                    ))}
                  </div>
                  <div className="insights-meta">
                    <span>Market Heat: <strong>{insights.market_heat}</strong></span>
                    <span>Trend: <strong>{insights.trend}</strong></span>
                    <span>DOM: <strong>{insights.days_on_market.toFixed(0)} days</strong></span>
                  </div>
                </div>
              )}
            </div>

            <div
              id="panel-ai-insights"
              role="tabpanel"
              aria-labelledby="tab-ai-insights"
              hidden={activeTab !== 'ai-insights'}
            >
              <div className="ai-insights-content">
                {aiLoading ? (
                  <div className="ai-loading-state">
                    <div className="ai-loading-spinner"></div>
                    <p>Generating AI insights...</p>
                  </div>
                ) : aiInsights ? (
                  <div className="ai-insights">
                    <p>{aiInsights}</p>
                  </div>
                ) : aiError ? (
                  <div className="error-message">{aiError}</div>
                ) : (
                  <div className="empty-state">Search a market above to generate AI insights.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
